/* @flow */

import { SessionStore } from "../uidispatcherapi/SessionStore";
import FetchArticlesAction from "../actions/FetchArticlesAction";
import * as ArticleClient from "../clients/ArticleClient";
import TrackingUtil from "../utils/TrackingUtil";
import * as ArticleCache from "../utils/ArticleCache";
import { Store } from "./Store";
import { BettyVariant } from "../types/EvaluateArticleNew";
import { getVariantIdFromBundleId, isEmpty } from "../utils/util";

export type LoadingState = "requested" | "loading" | "missing" | "error";
// After the UI split, we don't need timestamp anymore and
// can just use BettyVariant instead of FetchedArticle.
export type FetchedArticle = {
    imageUrl: string | undefined | null;
    variantId: {
        articleNumber: string;
        bettyVariantId: string;
        variantNumber: string;
    };
    bundles: {
        [bundleNumber: string]: any;
    };
    imageUrlL?: string;
    description?: string;
    timestamp?: number;
};
export type ArticleStoreEntry =
    | BettyVariant
    | FetchedArticle
    | {
          state: LoadingState;
      };
export type ArticleStoreEntryMap = {
    [variantId: string]: ArticleStoreEntry;
};

let articleMap: ArticleStoreEntryMap = {};

let isLoading = false;
let stopArticleLoading = false;
let currentStoreId: string | null = null;
let currentLoggedIn = false;
let currentCustomerId: string | null = null;

// FIXME (EK): Do not provide the map in the trigger -> rewrite usages in other stores
class ArticleStore extends Store {
    constructor() {
        super();

        // FIXME(FD) get rid of action
        this.listenTo(FetchArticlesAction, this.fetchArticle as any);
        this.listenTo(SessionStore, this.onSessionStoreTrigger);
        this.onSessionStoreTrigger();
    }

    onSessionStoreTrigger() {
        const loggedIn = SessionStore.loggedIn();
        const storeId = SessionStore.getCustomerStore();
        const customerId = SessionStore.getCustomerId();
        if (
            (storeId && storeId !== currentStoreId) ||
            customerId !== currentCustomerId ||
            currentLoggedIn !== loggedIn
        ) {
            ArticleCache.clear();
            articleMap = {};
            currentStoreId = storeId;
            currentLoggedIn = loggedIn;
            currentCustomerId = customerId;
        }
    }

    fetchArticle(ids: string[]) {
        this.fetchArticlesWithPreselection(ids, {});
    }

    fetchArticleWithBundleId(ids: string[]) {
        this.fetchArticlesWithPreselection(
            ids.map((id) => {
                return getVariantIdFromBundleId(id);
            }),
            {}
        );
    }

    fetchArticlesWithPreselection(
        ids: string[],
        variantLookupMap: {
            [x: string]: string;
        }
    ) {
        let idsToFetch = [] as string[];
        if (!stopArticleLoading) {
            const idsFromCache = this.handleCachedArticles(ids);
            idsToFetch = ids.filter(id => !idsFromCache.includes(id));
            if (isEmpty(idsToFetch)) {
                // all cache hits should trigger the store!
                this.trigger(articleMap);
            }
        }
        if (isEmpty(idsToFetch)) {
            stopArticleLoading = false;
            isLoading = false;
            return;
        }

        idsToFetch.forEach((id) => {
            articleMap[id] = { state: "requested" };
        });

        // load articles in chunks to speed up the first display of articles.
        // Overall load time of a big list might increase, but the users
        // perception will be that the load is faster.
        const chunkSize = 12;
        const chunk = idsToFetch.slice(0, chunkSize);
        chunk.forEach((id) => {
            articleMap[id] = { state: "loading" };
        });
        ArticleClient.fetchArticle(chunk, SessionStore.getCustomerStore(), SessionStore.getCustomerId())
            .then((response) => {
                this.loadArticleCompleted(response, chunk, variantLookupMap);
                this.fetchArticlesWithPreselection(idsToFetch.slice(chunkSize), variantLookupMap);
            })
            .catch((e) => this.loadArticlesFailed(e, chunk));
        this.trigger(articleMap);
    }

    decideOnTimeToCache(variant: { [x: string]: any }) {
        const bundleWithPriceFail = Object.values(variant?.bundles ?? {}).some((bundle: any) => {
            return Object.values(bundle?.stores ?? {}).some((store: any) => {
                const priceInfo = store && store.sellingPriceInfo;
                return !priceInfo || priceInfo.explanation;
            });
        });
        const bundleWithFlash = Object.values(variant.bundles ?? { }).some((bundle: any) => {
            return Object.values(bundle?.stores ?? {}).some((store: any) => {
                const priceInfo = store && store.sellingPriceInfo;
                return priceInfo && Object.values(priceInfo.dnrInfo ?? {}).some((dnrInfo: any) => dnrInfo.flashSalesCounter);
            });
        });
        return bundleWithPriceFail || bundleWithFlash ? 60 : 600;
    }

    loadArticlesFailed(error: any, requestedChunk: string[]) {
        TrackingUtil.trackUiErrorSd("ArticleStore", "Fetching articles from evaluate.article failed.", error);
        (requestedChunk ?? []).forEach((id) => {
            articleMap[id] = { state: "error" };
        });
        this.trigger(articleMap);
    }

    loadArticleCompleted(
        response: any,
        requestedChunk: string[],
        variantLookupMap: {
            [x: string]: string;
        }
    ) {
        const articles = response.result;
        const resultVariants = {} as { [variantId: string]: any };
        Object.values(articles).forEach((article: any) => {
            Object.values(article.variants).forEach((variant: any) => {
                const bettyVariantId = variant.variantId.bettyVariantId;
                const fetchedId = variantLookupMap[bettyVariantId];
                if (fetchedId) {
                    resultVariants[fetchedId] = variant;
                } else {
                    resultVariants[bettyVariantId] = variant;
                }
            });
        });
        (requestedChunk ?? []).forEach((id) => {
            if (resultVariants[id]) {
                articleMap[id] = resultVariants[id];
                ArticleCache.put(id, resultVariants[id], this.decideOnTimeToCache(resultVariants[id]));
            } else {
                articleMap[id] = { state: "missing" };
            }
        });
        this.trigger(articleMap);
    }

    stopArticleLoading() {
        if (isLoading) {
            stopArticleLoading = true;
        }
    }

    handleCachedArticles(ids: string[]) {
        const cachedData = ArticleCache.getAll(ids);
        if (!isEmpty(cachedData.ids)) {
            cachedData.ids.forEach((id) => {
                articleMap[id] = cachedData.data[id];
            });
        }
        return cachedData.ids;
    }

    getArticleData(ids: string[]) {
        return ArticleCache.getAll(ids);
    }

    getOneArticleData(id: string): ArticleStoreEntry | undefined | null {
        return articleMap[id];
    }

    getOneArticleDataWithBundleId(id: string): ArticleStoreEntry | undefined | null {
        return articleMap[getVariantIdFromBundleId(id)];
    }

    getArticleMap(): ArticleStoreEntryMap {
        return articleMap;
    }

    // for testing
    _reset() {
        articleMap = {};
    }

    _getCurrentStoreId() {
        return currentStoreId;
    }

    _getCurrentCustomerId() {
        return currentCustomerId;
    }

    _getCurrentLoggedIn() {
        return currentLoggedIn;
    }
}

export default ArticleStore;
